import { Price } from "../services/searchService";

// Night window and state-based GST split mirror FusionCab's admin-side
// booking logic (fusion_admindash CreateBookingPage.tsx / backend
// calculationUtils.js) - 22:00-06:00 pickup counts as night, and GST is
// CGST+SGST 2.5% each for Telangana-origin trips, IGST 5% otherwise.
const NIGHT_START_HOUR = 22;
const NIGHT_END_HOUR = 6;
const CGST_SGST_RATE = 0.025;
const IGST_RATE = 0.05;
const HOME_STATE = "telangana";

export interface FareBreakdown {
  distanceKm: number;
  includedKm: number;
  extraKm: number;
  isNight: boolean;
  baseFare: number;
  extraFare: number;
  nightCharges: number;
  tollCharges: number;
  parkingCharges: number;
  driverCharges: number;
  waitingCharges: number;
  stateTax: number;
  greenTax: number;
  totalFareBeforeTax: number;
  sgst: number;
  cgst: number;
  igst: number;
  totalFare: number;
}

export function isNightTime(pickupTime: string): boolean {
  if (!pickupTime) return false;
  const hours = parseInt(pickupTime.split(":")[0], 10);
  if (isNaN(hours)) return false;
  return hours >= NIGHT_START_HOUR || hours < NIGHT_END_HOUR;
}

// Inclusive calendar day count between two "YYYY-MM-DD" dates - e.g. pickup
// Monday, return Thursday = 4 days (Mon, Tue, Wed, Thu). Multi-City's
// per-day km minimum (see calculateMultiCityFare) is billed against this,
// not the number of nights.
function inclusiveDayCount(startDate?: string, endDate?: string): number {
  if (!startDate || !endDate) return 1;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffDays = Math.round((end.getTime() - start.getTime()) / 86400000);
  return Math.max(1, diffDays + 1);
}

// Flat, once-per-booking charges from the Plan - identical for every trip
// type, including Multi-City (only night charges get per-leg treatment
// there - see calculateMultiCityFare).
function computeFlatCharges(plan: Price["Plan"]) {
  return {
    tollCharges: plan.is_toll ? plan.toll_charges : 0,
    parkingCharges: plan.is_parking ? plan.parking_charges : 0,
    driverCharges: plan.is_driver ? plan.driver_charges : 0,
    waitingCharges: plan.is_waiting ? plan.waiting_charges : 0,
    stateTax: plan.is_tax ? plan.state_tax : 0,
    greenTax: plan.is_tax ? plan.green_tax : 0,
  };
}

// GST is charged only on the fare/service components (base fare, extra
// fare, driver allowance, night charges incl. night base fare) - toll,
// parking, state/interstate tax and green tax are statutory/pass-through
// charges and stay untaxed, added on top of totalFareBeforeTax as-is.
function computeGst(taxableAmount: number, totalFareBeforeTax: number, sourceState?: string) {
  const isHomeState = (sourceState || "").trim().toLowerCase() === HOME_STATE;
  const sgst = isHomeState ? taxableAmount * CGST_SGST_RATE : 0;
  const cgst = isHomeState ? taxableAmount * CGST_SGST_RATE : 0;
  const igst = isHomeState ? 0 : taxableAmount * IGST_RATE;
  return { sgst, cgst, igst, totalFare: totalFareBeforeTax + sgst + cgst + igst };
}

/**
 * price.fixed_fare is a per-km rate (confirmed by admin's own Price preview
 * calculator: fixed_fare * distance), applied to the plan's included/quoted
 * distance for a stable base fare; any distance beyond that (e.g. from a
 * live Google Maps route) is billed at price.extra_fare per km.
 */
export function calculateFare(
  price: Price,
  distanceKm: number | undefined,
  pickupTime: string,
  sourceState?: string
): FareBreakdown {
  const plan = price.Plan;
  const includedKm = plan.distance;
  const effectiveDistanceKm = distanceKm ?? includedKm;
  const extraKm = Math.max(0, effectiveDistanceKm - includedKm);

  const baseFare = price.fixed_fare * includedKm;
  const extraFare = extraKm * price.extra_fare;

  const night = isNightTime(pickupTime);
  const nightCharges = night
    ? price.night_base_fare + (plan.is_night ? plan.night_charges : 0)
    : 0;

  const { tollCharges, parkingCharges, driverCharges, waitingCharges, stateTax, greenTax } =
    computeFlatCharges(plan);

  const taxableAmount = baseFare + extraFare + nightCharges + driverCharges + waitingCharges;
  const totalFareBeforeTax =
    baseFare +
    extraFare +
    nightCharges +
    tollCharges +
    parkingCharges +
    driverCharges +
    waitingCharges +
    stateTax +
    greenTax;

  const { sgst, cgst, igst, totalFare } = computeGst(taxableAmount, totalFareBeforeTax, sourceState);

  return {
    distanceKm: effectiveDistanceKm,
    includedKm,
    extraKm,
    isNight: night,
    baseFare,
    extraFare,
    nightCharges,
    tollCharges,
    parkingCharges,
    driverCharges,
    waitingCharges,
    stateTax,
    greenTax,
    totalFareBeforeTax,
    sgst,
    cgst,
    igst,
    totalFare,
  };
}

// A single leg of a Multi-City itinerary's fare calculation - stops carry
// no per-leg time (see types/stop.ts), so this is just a distance.
export interface FareLeg {
  distanceKm: number;
}

/**
 * Multi-City fare: total distance is every forward leg (source->stop1->...
 * ->destination) PLUS the return leg back to source. Unlike a single-leg
 * trip, `plan.distance` here is the *per-day* minimum km guarantee (a
 * multi-day itinerary needs a floor that scales with trip length) - the
 * billed/included km is whichever is higher: that per-day minimum times the
 * number of days, or the actual itinerary distance. Night charges are
 * decided by `pickupTime` alone (same as every other trip type - see
 * calculateFare), applied at most once for the whole itinerary, since
 * individual stops/legs don't carry their own time. Every other charge
 * (toll/driver/waiting/parking/tax) stays a single flat amount. The return
 * leg's distance is a pricing input only - never surfaced as its own line
 * item, it just contributes to the same aggregate totals below.
 */
export function calculateMultiCityFare(
  price: Price,
  legs: FareLeg[],
  pickupTime: string,
  sourceState?: string,
  departureDate?: string,
  returnDate?: string
): FareBreakdown {
  const plan = price.Plan;
  const days = inclusiveDayCount(departureDate, returnDate);
  const minItineraryKm = plan.distance * days;
  const totalDistanceKm = legs.reduce((sum, leg) => sum + leg.distanceKm, 0);
  const effectiveDistanceKm = totalDistanceKm || minItineraryKm;
  const includedKm = Math.max(minItineraryKm, effectiveDistanceKm);
  const extraKm = Math.max(0, effectiveDistanceKm - includedKm);

  const baseFare = price.fixed_fare * includedKm;
  const extraFare = extraKm * price.extra_fare;

  const night = isNightTime(pickupTime);
  const nightCharges = night
    ? price.night_base_fare + (plan.is_night ? plan.night_charges : 0)
    : 0;

  const { tollCharges, parkingCharges, driverCharges, waitingCharges, stateTax, greenTax } =
    computeFlatCharges(plan);

  const taxableAmount = baseFare + extraFare + nightCharges + driverCharges + waitingCharges;
  const totalFareBeforeTax =
    baseFare +
    extraFare +
    nightCharges +
    tollCharges +
    parkingCharges +
    driverCharges +
    waitingCharges +
    stateTax +
    greenTax;

  const { sgst, cgst, igst, totalFare } = computeGst(taxableAmount, totalFareBeforeTax, sourceState);

  return {
    distanceKm: effectiveDistanceKm,
    includedKm,
    extraKm,
    isNight: night,
    baseFare,
    extraFare,
    nightCharges,
    tollCharges,
    parkingCharges,
    driverCharges,
    waitingCharges,
    stateTax,
    greenTax,
    totalFareBeforeTax,
    sgst,
    cgst,
    igst,
    totalFare,
  };
}
